import { RubyGemMark } from '../../components/RubyGemMark';
import React from 'react';
import { Brain, RefreshCw } from 'lucide-react';
import TimeRangePicker from '../../components/credits/TimeRangePicker';
import type { TimeRangeValue } from '../../services/aiControlApi';
import { AI_TABS, type AiTabId } from './aiTabs';

export interface AiControlHeaderStat {
  label: string;
  value: string;
  tone?: 'white' | 'emerald' | 'blue' | 'amber' | 'rose';
}

const TONES: Record<NonNullable<AiControlHeaderStat['tone']>, string> = {
  white: 'text-white',
  emerald: 'text-emerald-400',
  blue: 'text-blue-400',
  amber: 'text-amber-400',
  rose: 'text-rose-400',
};

interface Props {
  stats: AiControlHeaderStat[];
  activeTab: AiTabId;
  onTabChange: (t: AiTabId) => void;
  timeRange: TimeRangeValue;
  onTimeRangeChange: (v: TimeRangeValue) => void;
  showTimeRange: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}

/**
 * Page-header standard: full-bleed black banner, desktop-only stat cluster,
 * ruby gradient rule, emerald pill tabs. Ruby — never purple.
 */
const AiControlHeader: React.FC<Props> = ({
  stats, activeTab, onTabChange, timeRange, onTimeRangeChange,
  showTimeRange, onRefresh, refreshing,
}) => (
  <div className="bg-black">
    <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 flex-shrink-0">
        <Brain size={24} className="text-white" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-gray-400">AI Control Center</p>
          <h1 className="text-base md:text-xl font-bold text-white">
            {AI_TABS.find((t) => t.id === activeTab)?.label ?? 'AI Control Center'}
          </h1>
        </div>
      </div>

      {stats.length > 0 && (
        <div className="hidden md:flex items-center gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className={`text-xl font-bold ${TONES[s.tone ?? 'white']}`}>{s.value}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-shrink-0">
        {showTimeRange && (
          <TimeRangePicker value={timeRange} onChange={onTimeRangeChange} compact />
        )}
        <button
          type="button"
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all min-h-[44px] bg-white text-black hover:bg-gray-100"
        >
          {refreshing ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
    </div>

    {/* Emerald pill tabs — the platform tab standard. */}
    <div className="px-4 md:px-6 pb-3 flex items-center gap-2 overflow-x-auto scrollbar-hide">
      {AI_TABS.map((t) => {
        const Icon = t.icon;
        const active = activeTab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onTabChange(t.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors min-h-[36px] border ${
              active
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'bg-transparent border-white/20 text-gray-300 hover:bg-white/10'
            }`}
          >
            <Icon size={14} />
            {t.label}
          </button>
        );
      })}
    </div>

    {/* Ruby gradient rule — the super-admin identity line. */}
    <div style={{ background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' }} />
  </div>
);

export default AiControlHeader;
